import * as R from 'ramda'
import {
  generateWappSessionId,
  WappSessionCreator,
} from 'weblite-web-wapprunner'
import {
  eventHistoryChanged,
  eventWappVisibilityChanged,
  HistoryEvent,
  Meta,
  WappMode,
} from '@weblite-wapps/protocol'
import {
  getPanelHistory,
  getSectionsHistoryTop,
  getSectionTop,
  HistoryItemState,
  listen,
  PanelHistoryItem,
  popFromPanel,
  popFromSectionsHistory,
  popPanelFromSection,
  pushToPanel,
  replaceInPanel,
  resetPanel,
} from 'weblite-router'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { WappPanelState, WappPanelDelimiterState } from './types'
import { desktopAppApi } from '$scenes/components/app/app.desktop.api'
import { showWappOverlayLoadingModal } from '$comp/modals/loading-modal'
import { sendEventToWapp } from '$logic/wapp-communication/wapp-communication.publish'
import { isValidStaticWapp } from '$logic/wapp-panels/static-wapp-panels.utils'
import { resolveAwaitingParent } from '$logic/wapp-communication/helper/awaitingParentsForOutput'
import { isDesktopSize } from '$func/environmentInfo'
import { asyncDelay } from '$func/delay'

export class WappPanel {
  protected static wappMode = WappMode.MAIN
  static getPanelHistory = () => getPanelHistory('wapp')

  static pushToPanel = (data: WappPanelState) =>
    pushToPanel('wapp', 'wapp', data)

  static replaceInPanel = (data: WappPanelState) =>
    replaceInPanel('wapp', 'wapp', data)

  static popFromPanel = (justPop = false) => {
    if (justPop) {
      popFromPanel('wapp')
      return
    }

    const panelHistory = this.getPanelHistory()
    const [item, lastItem] = panelHistory.slice(-2)

    if (!isDesktopSize() && (lastItem === 'INIT' || !item || item === 'INIT')) {
      popFromSectionsHistory()
      // TODO[router]: replace with onRouterAnimationEnd when implemented
      setTimeout(() => popFromPanel('wapp'), 400)
    } else popFromPanel('wapp')
  }

  static resetPanel = (...initialData: HistoryItemState[]) =>
    resetPanel('wapp', ...initialData)

  static getTopWappMeta() {
    return (
      R.findLast(
        item => item !== 'INIT' && item.state?.wappSessionId,
        this.getPanelHistory(),
      ) as PanelHistoryItem
    )?.state as WappPanelDelimiterState
  }

  static getTopWappHistory(): WappPanelState[] {
    const panelHistory = this.getPanelHistory()
    const topWappStartIndex = R.findLastIndex(
      item => item !== 'INIT' && item.state?.wappSessionId,
      panelHistory,
    )

    return topWappStartIndex !== -1
      ? panelHistory
          .slice(topWappStartIndex + 1)
          // @ts-ignore
          .filter(state => state !== 'INIT')
          .map(item => (item as PanelHistoryItem).state)
      : []
  }

  protected static async adjustWappMeta(
    wappSessionId: string,
    adjustFunc: (oldState: WappPanelDelimiterState) => WappPanelDelimiterState,
  ) {
    const panelHistory = this.getPanelHistory()
    const wappStartIndex = panelHistory.findIndex(
      item => item !== 'INIT' && item.state?.wappSessionId === wappSessionId,
    )

    if (wappStartIndex < 0) return
    this.resetPanel(
      //@ts-ignore
      ...R.adjust(
        wappStartIndex,
        item => ({
          location: 'wapp',
          state: adjustFunc((item as HistoryItemState).state),
        }),
        panelHistory,
      ),
    )
  }

  static async openWapp(
    wapp: Omit<WappSessionCreator, 'mode'>,
    resetPanel = false,
    snapshot?: any,
  ) {
    const topWappMeta = this.getTopWappMeta()
    if (snapshot && topWappMeta?.wappSessionId)
      await this.adjustWappMeta(topWappMeta.wappSessionId, oldMeta => ({
        ...oldMeta,
        snapshot,
      }))

    if (resetPanel) this.resetPanel()

    const item = {
      ...wapp,
      wappSessionId: wapp?.wappSessionId || generateWappSessionId(),
    }

    return this.pushToPanel(item)
  }

  protected static async setParentOutput(parentWapp: Meta, output: unknown) {
    const { wappSessionId, mode } = parentWapp

    if (mode === WappMode.MAIN)
      return WappPanel.adjustWappMeta(
        wappSessionId,
        R.assoc('childOutput', output),
      )

    if (mode === WappMode.INLINE || isValidStaticWapp(parentWapp))
      return resolveAwaitingParent(wappSessionId, output)
  }

  static async closeTopWapp(output?: unknown) {
    const panelHistory = this.getPanelHistory()
    const topWappStartIndex = R.findLastIndex(
      item => item !== 'INIT' && item.state?.wappSessionId,
      panelHistory,
    )

    if (topWappStartIndex < 0) return
    desktopAppApi.setWappPanelIsFullscreen(false)

    const { parent } = (panelHistory[topWappStartIndex] as PanelHistoryItem)
      .state as WappPanelDelimiterState

    const parentWappSessionId = (parent as Meta)?.wappSessionId
    if (parentWappSessionId && output)
      await this.setParentOutput(parent, output)

    const updatedPanelHistory = this.getPanelHistory().slice(
      0,
      topWappStartIndex,
    )

    const thereIsAnotherWapp =
      R.findLastIndex(
        item => item !== 'INIT' && item.state?.wappSessionId,
        updatedPanelHistory,
      ) >= 0

    // Close panel if there is no other wapp
    if (!thereIsAnotherWapp) {
      if (!isDesktopSize()) {
        popFromSectionsHistory()

        // TODO[router]: refactor
        await asyncDelay(300)
      }

      this.resetPanel()
      popPanelFromSection(this.wappMode === WappMode.MAIN ? 'right' : 'overlay')
    } else {
      this.resetPanel(...updatedPanelHistory)
      if (
        updatedPanelHistory.length &&
        updatedPanelHistory[updatedPanelHistory.length - 1] === 'INIT'
      )
        this.popFromPanel(isDesktopSize())

      if (parent.mode === WappMode.OVERLAY) await WappOverlayLoadingModal.show()
    }
  }

  static isPanelVisible() {
    const activeSection = getSectionsHistoryTop()
    const rightSectionTop = getSectionTop('right')

    return (
      (isDesktopSize()
        ? activeSection !== 'comm'
        : activeSection === 'right') && rightSectionTop === 'wapp'
    )
  }

  static listenToHistoryEvents() {
    let lastOpenedWappSessionId: string | null
    let lastOpenedWappHistory: WappPanelState[]
    let lastIsVisible: boolean

    listen(event => {
      const topWappMeta = this.getTopWappMeta()
      const topWappSessionId = topWappMeta?.wappSessionId

      if (!topWappSessionId) {
        lastOpenedWappSessionId = null
        lastOpenedWappHistory = []
        return
      }

      // Update Visibility
      const isVisible = this.isPanelVisible()
      if (
        isVisible !== lastIsVisible ||
        topWappSessionId !== lastOpenedWappSessionId
      ) {
        if (
          topWappSessionId !== lastOpenedWappSessionId ||
          (!lastIsVisible && isVisible)
        )
          sendEventToWapp(
            eventWappVisibilityChanged(
              { visible: true },
              { ...topWappMeta, mode: WappMode.MAIN },
            ),
          )

        if (lastIsVisible && !isVisible)
          sendEventToWapp(
            eventWappVisibilityChanged(
              { visible: false },
              { ...topWappMeta, mode: WappMode.MAIN },
            ),
          )
      }

      lastIsVisible = isVisible

      if (lastOpenedWappSessionId !== topWappSessionId)
        lastOpenedWappSessionId = topWappSessionId
      else {
        const topWappHistory = this.getTopWappHistory()
        if (R.equals(topWappHistory, lastOpenedWappHistory)) return

        lastOpenedWappHistory = topWappHistory
        sendEventToWapp(
          eventHistoryChanged(
            {
              type: event as HistoryEvent,
              newHistory: topWappHistory,
            },
            {
              wappSessionId: topWappMeta.wappSessionId,
              wappId: topWappMeta.wappId,
              wisId: topWappMeta.wisId,
              mode: this.wappMode,
            },
          ),
        )
      }
    })
  }
}

export class OverlayWappPanel extends WappPanel {
  protected static wappMode = WappMode.OVERLAY

  static getPanelHistory = () => getPanelHistory('overlayWapp')

  static pushToPanel = (data: WappPanelState) =>
    pushToPanel('overlayWapp', 'wapp', data)

  static popFromPanel = () => popFromPanel('overlayWapp')

  static resetPanel = (...initialData: HistoryItemState[]) =>
    resetPanel('overlayWapp', ...initialData)

  static async openWapp(
    wapp: Omit<WappSessionCreator, 'mode'>,
    resetPanel = false,
  ) {
    WappOverlayLoadingModal.show()
    if (resetPanel) this.resetPanel()

    const item = {
      ...wapp,
      wappSessionId: wapp?.wappSessionId || generateWappSessionId(),
    }

    return this.pushToPanel(item)
  }

  protected static async setParentOutput(parentWapp: Meta, output: unknown) {
    const { wappSessionId, mode } = parentWapp

    if (mode === WappMode.OVERLAY)
      return this.adjustWappMeta(wappSessionId, R.assoc('childOutput', output))

    if (
      mode === WappMode.MAIN ||
      mode === WappMode.INLINE ||
      isValidStaticWapp(parentWapp)
    )
      return resolveAwaitingParent(wappSessionId, output)
  }
}

export class WappOverlayLoadingModal {
  private static DELAY = 1000

  private static timer: any
  private static isShowing: boolean

  static show() {
    if (WappOverlayLoadingModal.isShowing) return

    WappOverlayLoadingModal.timer = setTimeout(async () => {
      await showWappOverlayLoadingModal()
      WappOverlayLoadingModal.isShowing = true
    }, WappOverlayLoadingModal.DELAY)
  }

  static async hide() {
    clearTimeout(WappOverlayLoadingModal.timer)

    if (WappOverlayLoadingModal.isShowing) await closeCurrentModal()
    WappOverlayLoadingModal.isShowing = false
  }
}
